import type { Control, ControlOption, ControlSelections } from '../types/controls';

/** How many values a control holds at once. Only a multi-select has a cap. */
export const selectionCapOf = (control: Control): number | undefined =>
  control.multiple ? (control.maxSelections ?? undefined) : undefined;

/**
 * What a control left blank stands for. A capped control stands for all its options when they fit
 * under the cap, and for nothing when they do not: past the cap there is no "all of them" to draw,
 * so the user has to choose. An uncapped control keeps its blank as is.
 */
export const blankSelectionOf = (control: Control, options: ControlOption[]): string[] => {
  const cap = selectionCapOf(control);
  return cap !== undefined && options.length <= cap ? options.map((option) => option.value) : [];
};

/** Whether a control still needs a pick: capped, left blank, and offering more than it can hold. */
export const needsPick = (control: Control, options: ControlOption[], value: string[]): boolean => {
  const cap = selectionCapOf(control);
  return cap !== undefined && value.length === 0 && options.length > cap;
};

/** The first control, in template order, the dashboard cannot render without a pick on. */
export const firstControlNeedingPick = (
  controls: Control[],
  options: Record<string, ControlOption[]>,
  staged: ControlSelections,
): Control | undefined =>
  controls.find((control) => needsPick(control, options[control.key] ?? [], staged[control.key] ?? []));
